// Accès typé à la source unique des questions fréquentes.
//
// Trois consommateurs, une seule liste :
//   · FAQAccordion  — l'accordéon affiché sur l'accueil
//   · SchemaOrg     — le balisage FAQPage
//   · search_faq    — l'outil WebMCP (lot 3)
//
// Google exige que le balisage corresponde au contenu visible ; deux listes
// séparées finiraient par diverger et le balisage deviendrait fautif. Le même
// raisonnement vaut, en plus grave, pour l'outil : un agent qui répond une
// chose que la page dit autrement fait passer le site pour incohérent, et
// personne ne s'en aperçoit puisque personne ne lit les deux.
//
// Le JSON est régénéré au build depuis Supabase (scripts/sync-faq.mjs). Même
// dispositif que les communes, et pour les mêmes raisons : Node lit un JSON
// sans compilation, et le build ne dépend pas du réseau.
import donnees from './faq.json';

/** Réponse affichée. `list` porte une énumération, quand la réponse en a une. */
export interface FaqItem {
  q: string;
  a: string;
  list?: string[];
}

/** Entrée complète, telle qu'elle vit en base et dans le JSON. */
export interface FaqEntree {
  id: string;
  question: string;
  reponse: string;
  categorie: string;
  /**
   * Mots-clés de recherche, en plus de ceux que porte déjà la question.
   *
   * Ils servent à `search_faq` et couvrent les formulations que personne
   * n'écrit dans une question mais que tout le monde emploie en la posant :
   * « combien de m3 » pour « volume », « décommander » pour « annulation ».
   */
  motsCles: string[];
  /** Page du site qui traite le sujet au long. */
  pageReference: string | null;
  /**
   * Ce qu'un agent doit faire quand la réponse ne suffit pas — ou n'existe pas.
   *
   * C'est le champ le plus important du modèle, et le moins intuitif. Une FAQ
   * ordinaire n'a que des réponses ; celle-ci doit aussi porter ses TROUS, et
   * dire quoi faire devant chacun. Sans ça, un modèle confronté à une question
   * sans réponse ne se tait pas : il improvise une réponse plausible, et une
   * condition d'annulation plausible est une condition d'annulation fausse.
   */
  escalade: string | null;
  /** Affichée dans l'accordéon de l'accueil. */
  surAccueil: boolean;
  ordre: number;
  statut: 'draft' | 'published';
  dateVerification: string;
  /** Énumération, quand la réponse en comporte une. */
  liste?: string[];
}

interface FichierFaq {
  escaladeParDefaut: string;
  categories: Array<{ id: string; libelle: string }>;
  questions: FaqEntree[];
}

const fichier = donnees as unknown as FichierFaq;

/** Toutes les entrées publiées, dans l'ordre éditorial. */
export const FAQ: FaqEntree[] = fichier.questions
  .filter((q) => q.statut === 'published')
  .sort((a, b) => a.ordre - b.ordre);

export const CATEGORIES_FAQ = fichier.categories;

/**
 * Ce qu'un agent doit répondre quand il ne sait pas, et qu'aucune entrée ne
 * porte d'escalade plus précise.
 *
 * Publié comme une donnée et non écrit dans une description d'outil : le
 * numéro et les horaires y figurent, et ils changent.
 */
export const ESCALADE_PAR_DEFAUT = fichier.escaladeParDefaut;

/**
 * Les questions de l'accueil, au format attendu par l'accordéon et le balisage.
 *
 * L'export garde son nom et sa forme : FAQAccordion et SchemaOrg ne changent
 * pas d'une ligne.
 */
export const FAQ_ACCUEIL: FaqItem[] = FAQ
  .filter((q) => q.surAccueil)
  .map((q) => ({ q: q.question, a: q.reponse, ...(q.liste ? { list: q.liste } : {}) }));

/** Entrées d'une catégorie donnée, dans l'ordre éditorial. */
export const parCategorie = (categorie: string): FaqEntree[] =>
  FAQ.filter((q) => q.categorie === categorie);
